import { hasNonNegativeBalance } from './unpaidLeaseCredit.js';

export function isCountableCyclePayment(payment) {
  const status = payment?.status;
  if (
    status === 3 ||
    status === 4 ||
    status === 'Rejected' ||
    status === 'Returned'
  ) {
    return false;
  }

  const purpose = payment?.purpose;
  if (
    purpose === 4 ||
    purpose === 'Sec Dep' ||
    purpose === 'Sec Deposit'
  ) {
    return false;
  }

  return true;
}

export function getCurrentCyclePeriod() {
  const options = { timeZone: 'America/New_York' };
  const nyTime = new Date().toLocaleString('en-US', options);
  const nyDate = new Date(nyTime);
  const currentDay = nyDate.getDate();
  const currentMonth = nyDate.getMonth();
  const currentYear = nyDate.getFullYear();

  let currentPeriodStart;
  let currentPeriodEnd;
  if (currentDay >= 25) {
    currentPeriodStart = new Date(currentYear, currentMonth, 25);
    currentPeriodEnd = new Date(currentYear, currentMonth + 1, 24);
  } else {
    currentPeriodStart = new Date(currentYear, currentMonth - 1, 25);
    currentPeriodEnd = new Date(currentYear, currentMonth, 24);
  }

  return { currentPeriodStart, currentPeriodEnd, nyDate };
}

export function formatDateForInput(date) {
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000,
  );
  return adjustedDate.toISOString().split('T')[0];
}

export function formatUnpaidDate(dateString) {
  if (dateString === null || dateString === undefined) {
    return '--/--/--';
  }
  const options = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/New_York',
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
}

export function formatUnpaidCurrency(amount) {
  const n = Number(amount);
  if (!n) return '$0';
  const rounded = Math.round(n);
  const abs = Math.abs(rounded)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return rounded < 0 ? `-$${abs}` : `$${abs}`;
}

export function calculateUnpaidDueDate(currentPeriodStart) {
  const dueDate = new Date(currentPeriodStart);
  dueDate.setDate(1);
  dueDate.setMonth(dueDate.getMonth() + 1);
  return formatUnpaidDate(dueDate);
}

export function mapLeaseStateFields(property) {
  return {
    leaseState: property?.leaseState || '',
    leaseStateDescription: property?.leaseStateDescription || '',
    leaseStateForeground: property?.leaseStateForeground || '',
    leaseStateBackground: property?.leaseStateBackground || '',
    leaseStateDate: property?.leaseStateDate || '',
  };
}

function normalizeAddress(address) {
  return (address || '').trim().replace(/\s+/g, ' ');
}

function getNyDateKey(value) {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

export function getPaymentCycleDate(payment) {
  const effectiveDate = payment?.effectiveDate;
  if (effectiveDate != null && effectiveDate !== '') {
    return effectiveDate;
  }
  return payment?.paidOn;
}

function getCycleEndKey(currentPeriodEnd, asOfDate) {
  const periodEndKey = getNyDateKey(currentPeriodEnd);
  const asOfKey = getNyDateKey(asOfDate);
  if (!periodEndKey) return asOfKey;
  if (!asOfKey) return periodEndKey;
  return asOfKey < periodEndKey ? asOfKey : periodEndKey;
}

export function isDateInCycle(
  dateValue,
  currentPeriodStart,
  currentPeriodEnd,
  asOfDate,
) {
  const dateKey = getNyDateKey(dateValue);
  const startKey = getNyDateKey(currentPeriodStart);
  const endKey = getCycleEndKey(currentPeriodEnd, asOfDate);
  if (!dateKey || !startKey || !endKey) return false;
  return dateKey >= startKey && dateKey <= endKey;
}

export function isPaymentInCycle(
  payment,
  currentPeriodStart,
  currentPeriodEnd,
  asOfDate,
) {
  if (
    isDateInCycle(
      payment?.paidOn,
      currentPeriodStart,
      currentPeriodEnd,
      asOfDate,
    )
  ) {
    return true;
  }

  const effectiveDate = payment?.effectiveDate;
  if (effectiveDate != null && effectiveDate !== '') {
    return isDateInCycle(
      effectiveDate,
      currentPeriodStart,
      currentPeriodEnd,
      asOfDate,
    );
  }

  return false;
}

/** Previous rent cycle immediately before the current period start (25th–24th). */
export function getPreviousCyclePeriod(currentPeriodStart) {
  const previousPeriodStart = new Date(currentPeriodStart);
  previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
  const previousPeriodEnd = new Date(currentPeriodStart);
  previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);
  return { previousPeriodStart, previousPeriodEnd };
}

function isFeesPurpose(purpose) {
  return purpose === 3 || purpose === 'Fees';
}

/**
 * Payments that comprise "clearing": countable rent-cycle payments received in
 * the current or previous cycle that are not yet deposited (no depositGroup).
 * Works with raw API purpose/status codes or PaymentViewer mapped labels.
 */
export function isClearingPayment(
  payment,
  {
    currentPeriodStart,
    currentPeriodEnd,
    previousPeriodStart,
    previousPeriodEnd,
    nyDate,
  },
) {
  if (!payment) return false;

  const inCurrent = isPaymentInCycle(
    payment,
    currentPeriodStart,
    currentPeriodEnd,
    nyDate,
  );
  const inPrevious = isPaymentInCycle(
    payment,
    previousPeriodStart,
    previousPeriodEnd,
    previousPeriodEnd,
  );
  if (!inCurrent && !inPrevious) return false;
  if (!isCountableCyclePayment(payment)) return false;
  if (payment.depositGroup && String(payment.depositGroup).trim()) return false;
  if (isFeesPurpose(payment.purpose)) return false;
  return true;
}

export function getClearingCycleContext() {
  const { currentPeriodStart, currentPeriodEnd, nyDate } =
    getCurrentCyclePeriod();
  const { previousPeriodStart, previousPeriodEnd } =
    getPreviousCyclePeriod(currentPeriodStart);
  return {
    currentPeriodStart,
    currentPeriodEnd,
    previousPeriodStart,
    previousPeriodEnd,
    nyDate,
  };
}

export function filterClearingPayments(payments) {
  const ctx = getClearingCycleContext();
  return (payments || []).filter((payment) => isClearingPayment(payment, ctx));
}

export function sumClearingPayments(payments) {
  return filterClearingPayments(payments).reduce(
    (sum, payment) => sum + parseFloat(payment.amount || 0),
    0,
  );
}

/** @deprecated Use isPaymentInCycle for payments with effectiveDate */
export function isPaidOnInCycle(paidOn, currentPeriodStart, currentDate) {
  return isDateInCycle(paidOn, currentPeriodStart, currentDate, currentDate);
}

function getPaymentLeaseId(payment) {
  const leaseId = payment?.LID ?? payment?.leaseId;
  if (leaseId == null || leaseId === '') return null;
  const parsed = Number(leaseId);
  return Number.isNaN(parsed) ? null : parsed;
}

function getActiveLeases(propertySummary, nowTime = Date.now()) {
  return (propertySummary || []).filter((property) => {
    const startsOn = new Date(property.startsOn).getTime();
    const endsOn = new Date(property.endsOn).getTime();
    return startsOn <= nowTime && nowTime <= endsOn;
  });
}

function getPaidThisCycleKeys(
  payments,
  currentPeriodStart,
  currentPeriodEnd,
  asOfDate,
) {
  const paidLeaseIds = new Set();
  const paidAddresses = new Set();

  (payments || []).forEach((payment) => {
    if (
      !isPaymentInCycle(
        payment,
        currentPeriodStart,
        currentPeriodEnd,
        asOfDate,
      )
    ) {
      return;
    }
    if (!isCountableCyclePayment(payment)) {
      return;
    }

    const leaseId = getPaymentLeaseId(payment);
    if (leaseId != null) {
      paidLeaseIds.add(leaseId);
    }
    paidAddresses.add(normalizeAddress(payment.address));
  });

  return { paidLeaseIds, paidAddresses };
}

function hasPaidThisCycle(property, paidKeys) {
  const leaseId = Number(property?.leaseId);
  if (!Number.isNaN(leaseId) && paidKeys.paidLeaseIds.has(leaseId)) {
    return true;
  }
  return paidKeys.paidAddresses.has(normalizeAddress(property.address));
}

function resolveCycleParams({
  currentPeriodStart,
  currentPeriodEnd,
  currentDate,
}) {
  const period = getCurrentCyclePeriod();
  return {
    currentPeriodStart: currentPeriodStart ?? period.currentPeriodStart,
    currentPeriodEnd: currentPeriodEnd ?? period.currentPeriodEnd,
    asOfDate: currentDate ?? period.nyDate,
  };
}

/**
 * Active leases that have not yet paid rent for the current cycle (25th–24th)
 * and currently owe (negative ledger balance). Zero-balance and credit
 * tenants are omitted even if their last payment fell before the 25th.
 */
export function getUnpaidThisCycleProperties({
  propertySummary,
  payments,
  currentPeriodStart,
  currentPeriodEnd,
  currentDate,
}) {
  const cycle = resolveCycleParams({
    currentPeriodStart,
    currentPeriodEnd,
    currentDate,
  });
  const activeLeases = getActiveLeases(propertySummary);
  const paidKeys = getPaidThisCycleKeys(
    payments,
    cycle.currentPeriodStart,
    cycle.currentPeriodEnd,
    cycle.asOfDate,
  );

  return activeLeases.filter(
    (property) =>
      !hasPaidThisCycle(property, paidKeys) &&
      !hasNonNegativeBalance(property),
  );
}

export function getNegativeBalanceProperties(propertySummary) {
  return getActiveLeases(propertySummary).filter((property) => {
    return parseFloat(property.balance || 0) < 0;
  });
}

export function calculateUncollectedRent({
  propertySummary,
  payments,
  currentPeriodStart,
  currentPeriodEnd,
  currentDate,
}) {
  return getUnpaidThisCycleProperties({
    propertySummary,
    payments,
    currentPeriodStart,
    currentPeriodEnd,
    currentDate,
  }).reduce(
    (sum, property) => sum + parseFloat(property.monthlyBaseRent || 0),
    0,
  );
}

export function calculateUnpaidBalancesTotal({ propertySummary }) {
  return getNegativeBalanceProperties(propertySummary).reduce((sum, property) => {
    const balance = parseFloat(property.balance || 0);
    if (balance < 0) {
      return sum + Math.abs(balance);
    }
    return sum;
  }, 0);
}

function mapPropertyToLeaseRow(property, { bounced, currentPeriodStart }) {
  const rentOwedValue = parseFloat(property.monthlyBaseRent || 0);
  const balanceRaw = parseFloat(property.balance || 0);
  const balanceOwedValue = balanceRaw < 0 ? Math.abs(balanceRaw) : 0;

  return {
    property: property.address,
    tenant: property.tenants || bounced?.tenants || 'Unknown Tenant',
    amount: formatUnpaidCurrency(rentOwedValue),
    rentOwed: formatUnpaidCurrency(rentOwedValue),
    rentOwedValue,
    dueDate: calculateUnpaidDueDate(currentPeriodStart),
    balance: formatUnpaidCurrency(balanceRaw),
    balanceValue: Number.isFinite(balanceRaw) ? balanceRaw : 0,
    balanceOwed: formatUnpaidCurrency(balanceOwedValue),
    balanceOwedValue,
    phones: property.phones || '',
    emails: property.emails || '',
    sendEmails: property.sendEmails || property.emails || '',
    leaseId: property.leaseId ?? null,
    propertyId: property.propertyId ?? null,
    peopleIds: property.peopleIds || '',
    peopleId: (() => {
      const raw = String(property.peopleIds || '')
        .split(',')
        .map((s) => s.trim())
        .find((s) => s.length > 0);
      const n = raw != null ? Number(raw) : NaN;
      return Number.isNaN(n) ? null : n;
    })(),
    bouncedPayment: !!bounced,
    bounceAmount: bounced
      ? formatUnpaidCurrency(bounced.bouncedAmount)
      : undefined,
    bounceDate: bounced ? formatUnpaidDate(bounced.bouncedDate) : undefined,
    ...mapLeaseStateFields(property),
  };
}

export function buildUncollectedRentList({
  propertySummary,
  payments,
  bouncedPaymentLeases,
  currentPeriodStart,
  currentPeriodEnd,
  currentDate,
}) {
  const bouncedByAddress = new Map(
    (bouncedPaymentLeases || []).map((bouncedLease) => [
      bouncedLease.address,
      bouncedLease,
    ]),
  );

  const list = getUnpaidThisCycleProperties({
    propertySummary,
    payments,
    currentPeriodStart,
    currentPeriodEnd,
    currentDate,
  }).map((property) =>
    mapPropertyToLeaseRow(property, {
      bounced: bouncedByAddress.get(property.address),
      currentPeriodStart,
    }),
  );

  list.sort((a, b) => a.property.localeCompare(b.property));
  return list;
}

export function buildBalancesList({ propertySummary, currentPeriodStart }) {
  const list = getNegativeBalanceProperties(propertySummary).map((property) =>
    mapPropertyToLeaseRow(property, {
      bounced: null,
      currentPeriodStart,
    }),
  );

  list.sort((a, b) => a.property.localeCompare(b.property));
  return list;
}

export function buildUnpaidLeasesList({
  propertySummary,
  payments,
  bouncedPaymentLeases,
  currentPeriodStart,
  currentPeriodEnd,
  currentDate,
  mode = 'rent',
}) {
  if (mode === 'balance') {
    return buildBalancesList({ propertySummary, currentPeriodStart });
  }

  return buildUncollectedRentList({
    propertySummary,
    payments,
    bouncedPaymentLeases,
    currentPeriodStart,
    currentPeriodEnd,
    currentDate,
  });
}